import { create } from "zustand";

export type PrivateTeacher = {
  id: string;
  name: string | null;
  avatar: string | null;
  bio: string | null;
  specialties: string[];
  pricePerSession: number | null;
  currency: string;
  sessionDurationMin: number;
};

export type PrivateSlot = {
  startAt: string; // ISO UTC
  endAt: string; // ISO UTC
  localStart: string; // "HH:MM"
  localEnd: string; // "HH:MM"
};

export type PrivateBookingStep =
  | "teacher-select"
  | "calendar-select"
  | "guest-form"
  | "confirmed";

type PrivateBookingState = {
  step: PrivateBookingStep;
  selectedTeacher: PrivateTeacher | null;
  selectedDate: string | null; // "YYYY-MM-DD"
  selectedSlot: PrivateSlot | null;
  confirmedBookingId: string | null;

  setStep: (step: PrivateBookingStep) => void;
  selectTeacher: (teacher: PrivateTeacher) => void;
  setConfirmed: (bookingId: string) => void;
  /** Initialise the flow; if a teacherId is pre-configured, skip to calendar-select. */
  init: (presetTeacherId?: string) => void;
  reset: () => void;
};

export const usePrivateBookingStore = create<PrivateBookingState>((set) => ({
  step: "teacher-select",
  selectedTeacher: null,
  selectedDate: null,
  selectedSlot: null,
  confirmedBookingId: null,

  setStep: (step) => set({ step }),

  selectTeacher: (teacher) => set({ selectedTeacher: teacher, step: "calendar-select" }),

  setConfirmed: (bookingId) => set({ confirmedBookingId: bookingId, step: "confirmed" }),

  init: (presetTeacherId) =>
    set({
      step: presetTeacherId ? "calendar-select" : "teacher-select",
      selectedTeacher: presetTeacherId
        ? {
            id: presetTeacherId,
            name: null,
            avatar: null,
            bio: null,
            specialties: [],
            pricePerSession: null,
            currency: "EUR",
            sessionDurationMin: 60,
          }
        : null,
      selectedDate: null,
      selectedSlot: null,
      confirmedBookingId: null,
    }),

  reset: () =>
    set({
      step: "teacher-select",
      selectedTeacher: null,
      selectedDate: null,
      selectedSlot: null,
      confirmedBookingId: null,
    }),
}));
